import { Fragment, useMemo, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronsUpDown, Flame, Mountain, Scale, ShieldAlert, TrendingDown } from 'lucide-react';

export const fmtCap = (v) => { if (v == null) return '—'; const a = Math.abs(v), s = v < 0 ? '−' : ''; return a >= 1e12 ? `${s}$${(a / 1e12).toFixed(2)}T` : a >= 1e9 ? `${s}$${(a / 1e9).toFixed(1)}B` : `${s}$${(a / 1e6).toFixed(0)}M`; };
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(2)}%`);
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const money = (v) => (v == null ? 'n/a' : `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`);

const GRADE_TONE = {
  best: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 ring-emerald-500/40',
  good: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30',
  compounder: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 ring-emerald-500/40',
  pass: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30',
  neutral: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 ring-amber-500/30',
  bad: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-rose-500/30',
  worst: 'bg-rose-500/15 text-rose-800 dark:text-rose-300 ring-rose-500/40',
  fail: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-rose-500/30',
};

export const NOTE_META = {
  thin_retention: { icon: AlertTriangle, label: 'Not meaningful — almost nothing retained', tip: 'Retained earnings over the window were zero, negative, or under 2% of the starting market cap. Dividing a market-cap move by a near-zero number gives an unstable ratio, so no pass/fail is assigned.' },
  cyclical_spike: { icon: Flame, label: 'Peak-cycle spike', tip: 'Earnings jumped 3x+ (or swung from a loss) in the latest fiscal year, or revenue grew 60%+. ROE and P/E may be peak-of-cycle numbers — check the full cycle before trusting them (the Micron effect).' },
  commodity_cyclical: { icon: Mountain, label: 'Commodity cyclical', tip: 'Energy / Basic Materials — earnings track commodity prices, not a moat. A great ratio at the top of a boom can reverse.' },
  leverage_roe: { icon: Scale, label: 'Leverage-inflated ROE', tip: 'ROE above 50% with debt above equity — the return is partly a function of a thin equity base, not operating efficiency.' },
  loss_year_in_window: { icon: TrendingDown, label: 'Loss year in window', tip: 'At least one fiscal year in the test window posted a net loss. Retained earnings were summed net of that loss.' },
  anomalies_3plus: { icon: ShieldAlert, label: '3+ accounting anomalies', tip: 'The weekly Enron checklist flags 3 or more items for this company. Read the Accounting Anomalies card before buying.' },
};

export function GradeChip({ grade, children, testId }) {
  return (
    <span className={`inline-flex items-center justify-end rounded-md px-1.5 py-0.5 font-mono text-[11px] font-bold tabular-nums ring-1 ring-inset ${GRADE_TONE[grade] || 'text-muted-foreground ring-border/60'}`} data-testid={testId}>
      {children}
    </span>
  );
}

export function NoteIcons({ notes, ticker }) {
  if (!notes?.length) return <span className="text-muted-foreground/60">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      {notes.map((n) => {
        const m = NOTE_META[n];
        if (!m) return null;
        const Icon = m.icon;
        return <Icon key={n} className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" title={`${m.label} — ${m.tip}`} aria-label={m.label} data-testid={`buffett-note-${n}-${ticker}`} />;
      })}
    </span>
  );
}

// Column definitions: key, header, alignment, default sort direction, value accessor, cell renderer.
const COLS = [
  { key: 'rank', label: '#', align: 'right', dir: 'asc', value: (r, i) => i, cell: (r, i) => <span className="text-[10px] text-muted-foreground tabular-nums">{i + 1}</span> },
  { key: 'ticker', label: 'Stock', align: 'left', dir: 'asc', value: (r) => r.ticker, cell: (r, i, onSelect) => (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(r.ticker); }} className="font-mono font-bold text-primary hover:underline" data-testid={`buffett-link-${r.ticker}`}>{r.ticker}</button>
      <span className="block text-[11px] text-muted-foreground truncate max-w-[200px]">{r.name}</span>
    </>
  ) },
  { key: 'sector', label: 'Sector', align: 'left', dir: 'asc', hide: 'md', value: (r) => r.sector || '', cell: (r) => <span className="text-[11px] text-muted-foreground">{r.sector}</span> },
  { key: 'market_cap', label: 'Mkt cap', align: 'right', dir: 'desc', value: (r) => r.market_cap, cell: (r) => <span className="tabular-nums">{fmtCap(r.market_cap)}</span> },
  { key: 'yield', label: 'Yield', align: 'right', dir: 'desc', hide: 'sm', value: (r) => (r.pays_dividend ? r.yield : null), cell: (r) => <span className="tabular-nums">{r.yield != null && r.pays_dividend ? `${Number(r.yield).toFixed(2)}%` : '—'}</span> },
  { key: 'ratio', label: '$1 test', align: 'right', dir: 'desc', title: 'Change in market cap ÷ retained earnings over the window', value: (r) => r.ratio, cell: (r) => (
    <><GradeChip grade={r.grades?.ratio} testId={`buffett-ratio-${r.ticker}`}>{money(r.ratio)}</GradeChip><span className="block text-[9px] text-muted-foreground text-right">{r.window_years}-yr · FY{r.base_year}→{r.latest_year}</span></>
  ) },
  { key: 'roe', label: 'ROE', align: 'right', dir: 'desc', value: (r) => r.roe, cell: (r) => <GradeChip grade={r.grades?.roe}>{pct(r.roe)}</GradeChip> },
  { key: 'pe', label: 'P/E', align: 'right', dir: 'asc', value: (r) => r.pe, cell: (r) => <GradeChip grade={r.grades?.pe}>{num(r.pe)}</GradeChip> },
  { key: 'de', label: 'D/E', align: 'right', dir: 'asc', value: (r) => r.de, cell: (r) => <GradeChip grade={r.grades?.de}>{num(r.de)}</GradeChip> },
  { key: 'combo', label: 'Combo', align: 'center', dir: 'desc', title: 'Good buckets hit: ROE ≥ 15%, P/E ≤ 25, D/E < 1, $1 test ≥ $1', value: (r) => r.combo_score, cell: (r) => <span className="font-mono font-bold tabular-nums" data-testid={`buffett-combo-${r.ticker}`}>{r.combo_score}/4</span> },
  { key: 'notes', label: 'Notes', align: 'center', dir: 'desc', value: (r) => (r.notes || []).length, cell: (r) => <NoteIcons notes={r.notes} ticker={r.ticker} /> },
];
const HIDE = { md: 'hidden md:table-cell', sm: 'hidden sm:table-cell' };
const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };

function SortIcon({ active, dir }) {
  if (!active) return <ChevronsUpDown className="w-3 h-3 opacity-40" aria-hidden="true" />;
  return dir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary" aria-label="sorted ascending" /> : <ArrowDown className="w-3 h-3 text-primary" aria-label="sorted descending" />;
}

/**
 * sortable — array of column keys that can be sorted (header becomes a button).
 * hideCols  — array of column keys to omit entirely (e.g. 'yield' in the non-dividend view).
 */
export default function BuffettTestTable({ rows, testId, onSelect, emptyText = 'None in this group yet.', sortable = [], hideCols = [] }) {
  const [sort, setSort] = useState(null);
  const cols = COLS.filter((c) => !hideCols.includes(c.key));
  const sorted = useMemo(() => {
    if (!rows?.length || !sort) return rows || [];
    const col = COLS.find((c) => c.key === sort.key);
    const indexed = rows.map((r, i) => ({ r, i }));
    indexed.sort((a, b) => {
      const va = col.value(a.r, a.i), vb = col.value(b.r, b.i);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const c = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sort.dir === 'asc' ? c : -c;
    });
    return indexed.map((x) => x.r);
  }, [rows, sort]);
  if (!rows?.length) return <p className="text-xs text-muted-foreground px-2 py-3" data-testid={`${testId}-empty`}>{emptyText}</p>;
  const toggle = (col) => setSort((s) => (s?.key === col.key ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col.key, dir: col.dir }));
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {cols.map((c) => {
              const canSort = sortable.includes(c.key);
              const active = sort?.key === c.key;
              return (
                <th key={c.key} className={`px-2 py-1 ${ALIGN[c.align]} ${HIDE[c.hide] || ''}`} title={c.title} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                  {canSort ? (
                    <button type="button" onClick={() => toggle(c)} className={`inline-flex items-center gap-0.5 hover:text-foreground ${active ? 'text-foreground font-bold' : ''}`} data-testid={`${testId}-sort-${c.key}`} data-sort-dir={active ? sort.dir : 'none'}>
                      {c.label}<SortIcon active={active} dir={sort?.dir} />
                    </button>
                  ) : c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <Fragment key={r.ticker}>
              <tr className={`cursor-pointer hover:bg-amber-500/10 ${i % 2 ? 'bg-muted/30' : ''}`} onClick={() => onSelect(r.ticker)} data-testid={`buffett-row-${r.ticker}`}>
                {cols.map((c) => <td key={c.key} className={`px-2 py-1.5 ${ALIGN[c.align]} ${HIDE[c.hide] || ''}`}>{c.cell(r, i, onSelect)}</td>)}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
